import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { PiClockCounterClockwise } from "react-icons/pi";
import { HiOutlineX } from "react-icons/hi";
import API from "../../api";
import { capture } from "../../instrument";

const IgnoredTermsSection = () => {
  const queryClient = useQueryClient();
  const [newTerm, setNewTerm] = useState("");

  const { data: ignoredTerms, isError } = useQuery({
    queryKey: ["ignored-terms"],
    queryFn: async () => {
      const { ok, data, message } = await API.get("/ignored-term");
      if (!ok) throw new Error(message);
      return data;
    },
  });

  const refetchWhenLastMutationSettles = () => {
    if (queryClient.isMutating({ mutationKey: ["ignored-terms"] }) === 1) return queryClient.invalidateQueries({ queryKey: ["ignored-terms"] });
  };

  const addMutation = useMutation({
    mutationKey: ["ignored-terms"],
    mutationFn: (value) => API.post("/ignored-term", { value }),
    onMutate: async (value) => {
      await queryClient.cancelQueries({ queryKey: ["ignored-terms"] });
      queryClient.setQueryData(["ignored-terms"], (cached) => [...cached, { value }]);
    },
    onError: (error, value) => {
      capture(error);
      toast.error(error.message || "Impossible d'ignorer ce terme");
      setNewTerm(value);
    },
    onSettled: refetchWhenLastMutationSettles,
  });

  const deleteMutation = useMutation({
    mutationKey: ["ignored-terms"],
    mutationFn: (id) => API.delete(`/ignored-term/${id}`),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["ignored-terms"] });
      queryClient.setQueryData(["ignored-terms"], (cached) => cached.filter((term) => term._id !== id));
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Impossible de retirer ce terme");
    },
    onSettled: refetchWhenLastMutationSettles,
  });

  const onSubmit = (event) => {
    event.preventDefault();
    const value = newTerm.trim();
    if (!value) return;
    setNewTerm("");
    addMutation.mutate(value);
  };

  return (
    <section>
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[15px] font-medium text-gray-900">Termes ignorés</h3>
          <p className="mt-1 text-sm text-gray-500">
            {
              "Mots lus sur l'ordonnance qui ne sont pas des examens (bilan, à jeun, contrôle…). Sur les prochaines ordonnances, ils ne sont plus codés et ne déclenchent plus d'alerte. Majuscules, accents et ponctuation ne comptent pas."
            }
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
          onClick={() => window.open("/audit-log/ignoredterms", "_blank", "noopener,noreferrer,width=1000,height=800")}
          title="Journal d'audit"
        >
          <PiClockCounterClockwise className="h-5 w-5" />
        </button>
      </div>

      {isError && <p className="text-sm text-gray-500">Impossible de charger les termes ignorés.</p>}

      {!ignoredTerms && !isError && (
        <div className="flex justify-center py-16">
          <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
        </div>
      )}

      {ignoredTerms && (
        <>
          <form
            onSubmit={onSubmit}
            className="flex flex-wrap items-center gap-1.5 rounded-xl bg-white p-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06] focus-within:ring-2 focus-within:ring-primary-500/30"
          >
            {ignoredTerms.map((ignoredTerm) => (
              <span key={ignoredTerm._id || ignoredTerm.value} className="inline-flex h-7 items-center gap-0.5 rounded-md bg-gray-100 pl-2.5 pr-1 text-[13px] text-gray-800">
                {ignoredTerm.value}
                <button
                  type="button"
                  onClick={() => deleteMutation.mutate(ignoredTerm._id)}
                  disabled={!ignoredTerm._id}
                  title="Ne plus ignorer"
                  aria-label={`Ne plus ignorer « ${ignoredTerm.value} »`}
                  className="rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700"
                >
                  <HiOutlineX className="size-3.5" />
                </button>
              </span>
            ))}
            <input
              type="text"
              aria-label="Terme à ignorer"
              value={newTerm}
              onChange={(e) => setNewTerm(e.target.value)}
              placeholder={ignoredTerms.length > 0 ? "Ajouter un terme…" : "Ex. : bilan, à jeun, contrôle"}
              className="h-7 min-w-[12rem] flex-1 bg-transparent px-1.5 text-[13px] text-gray-900 outline-none placeholder:text-gray-400"
            />
          </form>
          <p className="mt-2 px-1 text-xs text-gray-500">
            {ignoredTerms.length} terme{ignoredTerms.length > 1 ? "s" : ""} · Entrée pour ajouter, × pour ne plus ignorer
          </p>
        </>
      )}
    </section>
  );
};

export default IgnoredTermsSection;
